import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import '@theme/dialog';

class StoreLookup extends Component {
  requiredRefs = ['buttonLabel'];

  #abortController = new AbortController();
  /** @type {import('@theme/dialog').DialogComponent | null} */
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    const eventContainer = this.closest('.shopify-section, dialog');
    const { signal } = this.#abortController;

    eventContainer?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleButtonClick = (event) => {
    event.preventDefault();

    const dialog = this.#getDialogComponent();
    if (!dialog) return;

    const hasSizePickerOptions = this.#populateSizePicker();
    const hasVisibleResults = this.#getResultsPanel()?.hidden === false;
    if (!hasSizePickerOptions && !hasVisibleResults) return;

    dialog.showDialog();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.dataset.productUrl = event.detail.data.newProduct.url;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const replacement = this.#findReplacement(event.detail.data.html);
    if (!replacement) return;

    const dialogWasOpen = this.#isDialogOpen();
    this.#applyReplacementState(replacement);

    if (dialogWasOpen) {
      this.#populateSizePicker();
    }
  };

  /**
   * @param {Document} html
   * @returns {HTMLElement | null}
   */
  #findReplacement(html) {
    const blockId = this.dataset.blockId;

    if (blockId) {
      const selector = `store-lookup[data-block-id="${CSS.escape(blockId)}"]`;
      const block = html.querySelector(selector);

      if (block instanceof HTMLElement) return block;
    }

    const fallback = html.querySelector('store-lookup');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {HTMLElement} replacement
   */
  #applyReplacementState(replacement) {
    this.dataset.buttonLabel = replacement.dataset.buttonLabel || '';
    this.dataset.selectionComplete = replacement.dataset.selectionComplete || 'false';
    this.dataset.productId = replacement.dataset.productId || this.dataset.productId || '';
    this.dataset.productUrl = replacement.dataset.productUrl || this.dataset.productUrl || '';
    this.dataset.sizeOptionNames = replacement.dataset.sizeOptionNames || '';

    this.refs.buttonLabel.textContent = this.dataset.buttonLabel;

    const resultsPanel = this.#getResultsPanel();

    if (resultsPanel) {
      resultsPanel.hidden = this.dataset.selectionComplete !== 'true';
    }

    const replacementResults = replacement.querySelector('[ref="resultsContent"]');
    const resultsContent = this.#getResultsContent();

    if (replacementResults instanceof HTMLElement && resultsContent) {
      morph(resultsContent, replacementResults, { childrenOnly: true });
    }
  }

  #populateSizePicker() {
    const sizePickerContainer = this.#getSizePickerContainer();

    if (!sizePickerContainer) return false;

    sizePickerContainer.replaceChildren();
    sizePickerContainer.hidden = true;
    sizePickerContainer.classList.add('variant-picker');

    const originalPicker = this.#getOriginalVariantPicker();

    if (!originalPicker) return false;

    const sizeOptions = this.#getSizeOptions(originalPicker);
    sizePickerContainer.hidden = sizeOptions.length === 0;

    sizeOptions.forEach((option) => {
      sizePickerContainer.appendChild(this.#cloneOptionForDialog(option));
    });

    return sizeOptions.length > 0;
  }

  /**
   * @returns {import('@theme/dialog').DialogComponent | null}
   */
  #getDialogComponent() {
    if (this.#dialogComponent && this.contains(this.#dialogComponent)) {
      return this.#dialogComponent;
    }

    const dialog = this.querySelector('dialog-component');
    this.#dialogComponent = dialog instanceof HTMLElement ? /** @type {import('@theme/dialog').DialogComponent} */ (dialog) : null;

    return this.#dialogComponent;
  }

  #isDialogOpen() {
    return Boolean(this.#getDialogComponent()?.refs?.dialog?.open);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSizePickerContainer() {
    const dialog = this.#getDialogComponent();
    const container = dialog?.querySelector('[ref="sizePickerContainer"]');
    return container instanceof HTMLElement ? container : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getResultsPanel() {
    const dialog = this.#getDialogComponent();
    const panel = dialog?.querySelector('[ref="resultsPanel"]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getResultsContent() {
    const dialog = this.#getDialogComponent();
    const content = dialog?.querySelector('[ref="resultsContent"]');
    return content instanceof HTMLElement ? content : null;
  }

  /**
   * @returns {Element | null}
   */
  #getOriginalVariantPicker() {
    const productId = this.dataset.productId;
    const container = this.closest('.shopify-section, dialog');
    if (!container) return null;

    if (productId) {
      const matchingPicker = container.querySelector(`variant-picker[data-product-id="${CSS.escape(productId)}"]`);
      if (matchingPicker) return matchingPicker;
    }

    return container.querySelector('variant-picker');
  }

  /**
   * @param {Element} originalPicker
   * @returns {HTMLElement[]}
   */
  #getSizeOptions(originalPicker) {
    const options = Array.from(originalPicker.querySelectorAll('.variant-option')).filter((option) => {
      return option instanceof HTMLElement && !option.classList.contains('variant-option--single');
    });

    if (options.length === 0) return [];

    const sizeOptionNames = this.#getSizeOptionNames();
    const matchingOptions = options.filter((option) => {
      const optionName = option.dataset.optionName?.trim().toLowerCase();

      return optionName ? sizeOptionNames.has(optionName) : false;
    });

    return matchingOptions.length > 0 ? matchingOptions : options;
  }

  /**
   * @returns {Set<string>}
   */
  #getSizeOptionNames() {
    return new Set(
      (this.dataset.sizeOptionNames || '')
        .split(',')
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean)
    );
  }

  /**
   * @param {HTMLElement} option
   * @returns {HTMLElement}
   */
  #cloneOptionForDialog(option) {
    const clone = /** @type {HTMLElement} */ (option.cloneNode(true));
    this.#remapCloneIds(clone);
    this.#deduplicateInputNames(clone);
    this.#attachProxyListeners(clone);

    return clone;
  }

  /**
   * @param {HTMLElement} clone
   */
  #remapCloneIds(clone) {
    clone.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      const clonedId = `${originalId}-store-lookup-clone`;

      element.id = clonedId;

      clone.querySelectorAll(`label[for="${originalId}"]`).forEach((label) => {
        label.setAttribute('for', clonedId);
      });
    });
  }

  /**
   * @param {HTMLElement} clone
   */
  #deduplicateInputNames(clone) {
    clone.querySelectorAll('input[type="radio"]').forEach((input) => {
      /** @type {HTMLInputElement} */ (input).name += '-store-lookup-clone';
    });
  }

  /**
   * @param {HTMLElement} clone
   */
  #attachProxyListeners(clone) {
    clone.querySelectorAll('input, select').forEach((input) => {
      input.addEventListener('change', this.#handleProxySelection);
    });
  }

  /**
   * @param {Event} event
   */
  #handleProxySelection = (event) => {
    const target = event.target;
    
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const originalPicker = this.#getOriginalVariantPicker();

    if (!originalPicker) return;

    const value = target.value;
    let originalInput = null;

    if (target instanceof HTMLInputElement) {
      const originalName = target.name.replace('-store-lookup-clone', '');
      originalInput = originalPicker.querySelector(`input[name="${CSS.escape(originalName)}"][value="${CSS.escape(value)}"]`);
    } else {
      originalInput = originalPicker.querySelector(`select[name="${CSS.escape(target.name)}"]`);
    }

    if (!(originalInput instanceof HTMLInputElement || originalInput instanceof HTMLSelectElement)) return;

    if (originalInput instanceof HTMLInputElement) {
      originalInput.checked = true;
    } else {
      originalInput.value = value;
    }

    originalInput.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

if (!customElements.get('store-lookup')) {
  customElements.define('store-lookup', StoreLookup);
}
